import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { useTrustedDevice } from "@/hooks/useTrustedDevice";
import PinGate from "@/components/PinGate";

interface ProtectedUserRouteProps {
  children: React.ReactNode;
}

export default function ProtectedUserRoute({ children }: ProtectedUserRouteProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pinVerified, setPinVerified] = useState(false);
  const [showPinGate, setShowPinGate] = useState(false);
  
  const { isCurrentDeviceTrusted, loading: deviceLoading } = useTrustedDevice(user);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
      }
      setLoading(false);
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
        // Reset PIN verification when user changes
        setPinVerified(false);
        setShowPinGate(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Handle trusted device logic
  useEffect(() => {
    if (user && !deviceLoading) {
      if (isCurrentDeviceTrusted && !pinVerified) {
        // Check if PIN was already verified in sessionStorage
        const pinVerifiedKey = `pin_verified_${user.id}`;
        const pinTimeKey = `pin_verified_time_${user.id}`;
        const isVerified = sessionStorage.getItem(pinVerifiedKey) === 'true';
        const verifiedTime = sessionStorage.getItem(pinTimeKey);
        
        // Check if PIN verification is recent (within 5 minutes)
        const isRecent = verifiedTime && (Date.now() - parseInt(verifiedTime)) < 5 * 60 * 1000;
        
        if (isVerified && isRecent) {
          console.log("✅ PIN already verified for trusted device, allowing access");
          setPinVerified(true);
          setShowPinGate(false);
        } else {
          console.log("🔒 Trusted device needs PIN verification");
          setShowPinGate(true);
        }
      } else if (!isCurrentDeviceTrusted) {
        // New device - let normal auth flow handle it
        setShowPinGate(false);
        setPinVerified(true);
      }
    }
  }, [user, isCurrentDeviceTrusted, deviceLoading, pinVerified]);

  if (loading || deviceLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">กำลังโหลด...</p>
      </div>
    );
  }

  if (!user) {
    // Check if this is a trusted device with PIN verification
    const urlParams = new URLSearchParams(window.location.search);
    const trustedUserId = urlParams.get('trusted_user_id');
    const pinVerified = sessionStorage.getItem(`pin_verified_${trustedUserId}`) === 'true';
    
    if (trustedUserId && pinVerified) {
      // Allow access for trusted device with verified PIN
      console.log("✅ Allowing access for trusted device with verified PIN");
      return <>{children}</>;
    }
    
    return <Navigate to="/auth" replace />;
  }

  // Show PIN gate for trusted devices that haven't verified PIN yet
  if (showPinGate && isCurrentDeviceTrusted && !pinVerified) {
    return (
      <PinGate
        user={user}
        onPinVerified={() => {
          setPinVerified(true);
          setShowPinGate(false);
        }}
        onUseOtherMethod={() => {
          // Clear PIN verification and redirect to auth
          setPinVerified(false);
          setShowPinGate(false);
          window.location.href = "/auth";
        }}
      />
    );
  }

  return <>{children}</>;
}